import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Container,
  MenuItem, Paper, Select, Stack, Typography,
} from "@mui/material";
import { db } from "../../firebase/firebase-config";
import Colors from "../../assets/styles/Colors";

const STATUS_OPTIONS = ["Todos", "paid", "processing", "pending_wallet", "pending_balance", "failed"];
const TYPE_OPTIONS   = [
  { value: "Todos",  label: "Todos los tipos"   },
  { value: "level1", label: "Venta Directa"     },
  { value: "level2", label: "Venta Indirecta"   },
];

const STATUS_META = {
  paid:            { label: "Pagado",      color: Colors.success  },
  processing:      { label: "Procesando",  color: Colors.info     },
  pending_wallet:  { label: "Sin wallet",  color: Colors.warning  },
  pending_balance: { label: "En cola",     color: Colors.warning  },
  failed:          { label: "Fallido",     color: Colors.error    },
};

function StatusChip({ status }) {
  const meta = STATUS_META[status] || { label: status, color: Colors.textMuted };
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{ backgroundColor: meta.color + "22", color: meta.color, border: `1px solid ${meta.color}44`, fontWeight: 700 }}
    />
  );
}

function fmt(n) { return typeof n === "number" ? n.toFixed(2) : "—"; }

function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function CommissionsView() {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [typeFilter, setTypeFilter]     = useState("Todos");
  const [partnerFilter, setPartnerFilter] = useState("");

  useEffect(() => {
    const unsub = db.collection("commissions").orderBy("createdAt", "desc").onSnapshot(
      snap => {
        setCommissions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => { setError("No se pudieron cargar las comisiones."); setLoading(false); },
    );
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    return commissions.filter(c => {
      if (statusFilter !== "Todos" && c.status !== statusFilter) return false;
      if (typeFilter   !== "Todos" && c.commissionType !== typeFilter) return false;
      if (partnerFilter.trim() && !c.partnerId?.toLowerCase().includes(partnerFilter.toLowerCase())) return false;
      return true;
    });
  }, [commissions, statusFilter, typeFilter, partnerFilter]);

  const totals = useMemo(() => ({
    pending: filtered.filter(c => c.status === "pending_wallet" || c.status === "pending_balance").reduce((s, c) => s + (c.commissionAmount ?? 0), 0),
    paid:    filtered.filter(c => c.status === "paid").reduce((s, c) => s + (c.commissionAmount ?? 0), 0),
    total:   filtered.reduce((s, c) => s + (c.commissionAmount ?? 0), 0),
  }), [filtered]);

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, background: `linear-gradient(135deg, ${Colors.bgSecondary} 0%, ${Colors.bgTertiary} 100%)`, border: `1px solid ${Colors.borderSecondary}` }}>
          <Typography variant="h5" sx={{ color: Colors.textPrimary }}>Comisiones IB</Typography>
          <Typography variant="body2" sx={{ color: Colors.textSecondary, mt: 0.5 }}>
            Historial de comisiones generadas por referidos. Colección: <code>commissions</code>
          </Typography>
        </Paper>

        {/* Summary cards */}
        {!loading && !error && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
            {[
              { label: "Total comisiones",   value: fmt(totals.total),   color: Colors.textPrimary },
              { label: "Pagadas",            value: fmt(totals.paid),    color: Colors.success },
              { label: "Pendientes/En cola", value: fmt(totals.pending), color: Colors.warning },
              { label: "Registros",          value: filtered.length,     color: Colors.textSecondary },
            ].map(card => (
              <Paper key={card.label} sx={{ flex: 1, p: 2, borderRadius: 2, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` }}>
                <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{card.label}</Typography>
                <Typography variant="h6" sx={{ color: card.color, mt: 0.5 }}>{card.value} {typeof card.value === "number" && card.label !== "Registros" ? "USDT" : card.label !== "Registros" ? " USDT" : ""}</Typography>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Filters */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <Select
            size="small"
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            sx={{ minWidth: 180, backgroundColor: Colors.bgSecondary, color: Colors.textPrimary, ".MuiOutlinedInput-notchedOutline": { borderColor: Colors.borderSecondary } }}
          >
            {TYPE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
          <Select
            size="small"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            sx={{ minWidth: 180, backgroundColor: Colors.bgSecondary, color: Colors.textPrimary, ".MuiOutlinedInput-notchedOutline": { borderColor: Colors.borderSecondary } }}
          >
            {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{STATUS_META[s]?.label ?? s}</MenuItem>)}
          </Select>
          <Box
            component="input"
            placeholder="Filtrar por partner ID…"
            value={partnerFilter}
            onChange={e => setPartnerFilter(e.target.value)}
            sx={{ px: 1.5, py: 1, borderRadius: 1, border: `1px solid ${Colors.borderSecondary}`, backgroundColor: Colors.bgSecondary, color: Colors.textPrimary, fontSize: 14, outline: "none", minWidth: 220 }}
          />
        </Stack>

        {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>}
        {!loading && error && <Typography color="error" variant="body2">{error}</Typography>}
        {!loading && !error && !filtered.length && (
          <Typography variant="body2" sx={{ color: Colors.textSecondary }}>Sin resultados para los filtros seleccionados.</Typography>
        )}

        {!loading && !error && filtered.length > 0 && (
          <Paper sx={{ borderRadius: 3, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}`, overflow: "hidden" }}>
            {/* Header row */}
            <Box sx={{ px: 3, py: 1.5, backgroundColor: Colors.bgTertiary, borderBottom: `1px solid ${Colors.borderSecondary}` }}>
              <Stack direction="row" spacing={2}>
                {["Fecha", "Socio IB", "Usuario referido", "Plan", "Monto plan", "Comisión", "Tipo", "Estado"].map(h => (
                  <Box key={h} sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>{h}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            {filtered.map(c => (
              <Box key={c.id} sx={{ px: 3, py: 1.75, borderBottom: `1px solid ${Colors.borderSecondary}`, transition: "background-color 0.15s", "&:hover": { backgroundColor: Colors.bgTertiary } }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{fmtDate(c.createdAt)}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: Colors.textPrimary, wordBreak: "break-all" }}>{c.partnerId ?? "—"}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: Colors.textSecondary, wordBreak: "break-all" }}>{c.referredUserId ?? "—"}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{c.planId ?? "—"}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: Colors.textPrimary }}>{c.planAmount ? `${fmt(c.planAmount)} USDT` : "—"}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: Colors.success, fontWeight: 700 }}>{c.commissionAmount ? `${fmt(c.commissionAmount)} USDT` : "—"}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Chip
                      label={c.commissionType === "level2" ? "Indirecta" : "Directa"}
                      size="small"
                      sx={{
                        backgroundColor: c.commissionType === "level2" ? Colors.info + "22" : Colors.success + "22",
                        color:           c.commissionType === "level2" ? Colors.info        : Colors.success,
                        border:          `1px solid ${c.commissionType === "level2" ? Colors.info : Colors.success}44`,
                        fontWeight: 700,
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StatusChip status={c.status} />
                  </Box>
                </Stack>
                {c.nowpaymentsPayoutId && (
                  <Typography variant="caption" sx={{ color: Colors.textMuted, display: "block", mt: 0.5 }}>
                    Payout ID: {c.nowpaymentsPayoutId}
                  </Typography>
                )}
                {c.errorMessage && (
                  <Typography variant="caption" sx={{ color: Colors.error, display: "block", mt: 0.5 }}>
                    Error: {c.errorMessage}
                  </Typography>
                )}
              </Box>
            ))}
          </Paper>
        )}
      </Container>
    </Box>
  );
}
